import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api, { resolveUploadUrl } from '../config/api';
import NewsCard from '../components/NewsCard';
import { getMatchLogos, getCompetitionLogo } from '../utils/teamLogos';
import { isActiveMemberForSeason } from '../utils/memberStatus';
import { formatMatchDateParts, formatMatchPhaseRange } from '../utils/matchDateTime';
import { getSalesPhaseTitle } from '../utils/salesPhaseTitle';
import InactiveMemberNotice from '../components/InactiveMemberNotice';
import { resolvePartnerLogoUrl } from '../utils/partnerLogos';
import { FaInfoCircle } from 'react-icons/fa';
import './Tickets.css';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, locale, language } = useLanguage();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rssArticles, setRssArticles] = useState([]);
  const [rssLoading, setRssLoading] = useState(true);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [matchLoading, setMatchLoading] = useState(true);
  const [standings, setStandings] = useState([]);
  const [standingsLoading, setStandingsLoading] = useState(true);
  const [heroImageUrl, setHeroImageUrl] = useState(null);
  const [partners, setPartners] = useState([]);
  const [useHeroBackgroundPhoto, setUseHeroBackgroundPhoto] = useState(() =>
    typeof window !== 'undefined' ? !window.matchMedia('(max-width: 768px)').matches : true
  );

  useEffect(() => {
    fetchRSSFeed();
    fetchUpcomingMatches();
    fetchSerieAStandings();
  }, []);

  useEffect(() => {
    fetchNews();
  }, [currentPage, language]);

  useEffect(() => {
    api.get('/settings/hero').then((res) => setHeroImageUrl(res.data.heroImageUrl)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/partners').then((res) => setPartners(Array.isArray(res.data) ? res.data : [])).catch(() => setPartners([]));
  }, []);

  const partnersWithLogos = useMemo(
    () => partners.filter((p) => Boolean(resolvePartnerLogoUrl(p.logoUrl))),
    [partners]
  );

  const partnersMarqueeDurationSec =
    partnersWithLogos.length === 0
      ? null
      : Math.max(20, Math.min(100, partnersWithLogos.length * 5));

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setUseHeroBackgroundPhoto(!mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--main-padding-top', '100px');
    return () => document.documentElement.style.removeProperty('--main-padding-top');
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/news?page=${currentPage}&limit=9&lang=${language}`);
      setNews(response.data.news);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRSSFeed = async () => {
    try {
      setRssLoading(true);
      // Use a CORS proxy to fetch the RSS feed
      const proxyUrl = 'https://api.allorigins.win/get?url=';
      const rssUrl = encodeURIComponent('https://www.tuttosport.com/rss/calcio/serie-a/juventus');
      
      const response = await fetch(proxyUrl + rssUrl);
      const data = await response.json();
      
      // Parse the RSS XML
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(data.contents, 'text/xml');
      
      // Get current month and year for filtering
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth(); // 0-11 (0 = January)
      
      // Function to decode HTML entities
      const decodeHtmlEntities = (text) => {
        if (!text) return '';
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value;
      };
      
      // Extract items from RSS feed and filter by current month
      const items = xmlDoc.querySelectorAll('item');
      const articles = Array.from(items)
        .map(item => {
          const title = item.querySelector('title')?.textContent || '';
          const link = item.querySelector('link')?.textContent || '';
          const description = item.querySelector('description')?.textContent || '';
          const pubDate = item.querySelector('pubDate')?.textContent || '';
          const mediaContent = item.querySelector('media\\:content, content')?.getAttribute('url') || '';
          
          // Parse publication date
          let articleDate = null;
          if (pubDate) {
            articleDate = new Date(pubDate);
          }
          
          // Clean title by removing HTML tags and decoding all entities
          const cleanTitle = decodeHtmlEntities(
            title.replace(/<[^>]*>/g, '') // Remove HTML tags first
          ).trim();
          
          // Clean description
          const cleanDescription = decodeHtmlEntities(
            description.replace(/<[^>]*>/g, '') // Remove HTML tags first
          ).substring(0, 150) + '...';
          
          return {
            title: cleanTitle,
            link,
            description: cleanDescription,
            pubDate,
            articleDate, // Store parsed date for filtering
            image: mediaContent
          };
        })
        .filter(article => {
          // Filter: only keep articles from current month and year
          if (!article.articleDate || isNaN(article.articleDate.getTime())) {
            return false; // Skip articles with invalid dates
          }
          
          const articleYear = article.articleDate.getFullYear();
          const articleMonth = article.articleDate.getMonth();
          
          return articleYear === currentYear && articleMonth === currentMonth;
        })
        .slice(0, 4); // Afficher 4 articles en cartes
      
      setRssArticles(articles);
    } catch (error) {
      console.error('Error fetching RSS feed:', error);
    } finally {
      setRssLoading(false);
    }
  };

  const fetchUpcomingMatches = async () => {
    try {
      setMatchLoading(true);
      const response = await api.get('/tickets');
      const tickets = response.data;

      const now = new Date();
      const nextTickets = tickets
        .filter((ticket) => ticket.active && new Date(ticket.date) > now)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 3);

      setUpcomingMatches(nextTickets);
    } catch (error) {
      console.error('Error fetching upcoming matches:', error);
    } finally {
      setMatchLoading(false);
    }
  };

  const fetchSerieAStandings = async () => {
    try {
      setStandingsLoading(true);
      const response = await api.get('/serie-a-standings');
      if (response.data.table && Array.isArray(response.data.table)) {
        setStandings(response.data.table);
      }
    } catch (error) {
      console.error('Error fetching Serie A standings:', error);
    } finally {
      setStandingsLoading(false);
    }
  };

  const formatDateCard = (dateString) => formatMatchDateParts(dateString, locale);

  const formatPhaseRange = (startDate, endDate) => formatMatchPhaseRange(startDate, endDate, locale);

  const isPhaseActive = (phase) => {
    const now = new Date();
    const start = new Date(phase.startDate);
    const end = new Date(phase.endDate);
    return now >= start && now <= end;
  };

  const canOrderTickets = !user || isActiveMemberForSeason(user);

  const handleOrderClick = (ticket) => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isActiveMemberForSeason(user)) {
      return;
    }
    navigate('/billets/paiement', { state: { ticket } });
  };

  const renderUpcomingMatchCard = (match) => {
    const { homeLogo, awayLogo, homeTeam, awayTeam } = getMatchLogos(match.matchTitle);
    const { dateLine, timeLine } = formatDateCard(match.date);

    return (
      <div key={match._id} className="ticket-card ticket-card-horizontal">
        <div className="ticket-card-left">
          <h3 className="ticket-card-title">{match.matchTitle}</h3>
          <p className="ticket-card-date">{dateLine}</p>
          <p className="ticket-card-time">{timeLine}</p>
          {match.stadium && (
            <p className="ticket-card-stadium">📍 {match.stadium}</p>
          )}
          <div className="ticket-card-actions">
            <button
              type="button"
              className="btn-ticket-info"
              onClick={() => handleOrderClick(match)}
              disabled={user && !canOrderTickets}
            >
              {t('tickets.infoPrice')}
            </button>
            <button
              type="button"
              className="btn-ticket-buy"
              disabled={match.status === 'full' || (user && !canOrderTickets)}
              onClick={() => handleOrderClick(match)}
            >
              {match.status === 'full' ? t('tickets.full') : t('tickets.buy')}
            </button>
          </div>
        </div>
        <div className="ticket-card-center">
          <div className="ticket-teams">
            <div className="team-logo-container">
              <img src={homeLogo} alt={homeTeam || 'Home'} className="team-logo" />
              <span className="team-name">{homeTeam}</span>
            </div>
            <div className="vs-section">
              {match.competition && getCompetitionLogo(match.competition) && (
                <img
                  src={getCompetitionLogo(match.competition)}
                  alt={match.competition || 'Competition'}
                  className="competition-logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <span className="vs-separator">VS</span>
            </div>
            <div className="team-logo-container">
              <img src={awayLogo} alt={awayTeam || 'Away'} className="team-logo" />
              <span className="team-name">{awayTeam}</span>
            </div>
          </div>
        </div>
        <div className="ticket-card-right">
          <h4 className="sales-phases-heading">{t('tickets.salesPhases')}</h4>
          {match.salesPhases && match.salesPhases.length > 0 ? (
            <ul className="sales-phases-list">
              {match.salesPhases.map((phase, index) => {
                const active = isPhaseActive(phase);
                return (
                  <li key={index} className={`sales-phase-item ${active ? 'active' : ''}`}>
                    <span className="phase-name">{getSalesPhaseTitle(phase, language)}</span>
                    <span className="phase-range">
                      {formatPhaseRange(phase.startDate, phase.endDate)}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="sales-phases-empty">{t('tickets.noPhases')}</p>
          )}
          {match.description && (
            <p className="home-next-match-desc">{match.description}</p>
          )}
        </div>
      </div>
    );
  };

  const heroResolved = heroImageUrl ? resolveUploadUrl(heroImageUrl) : null;
  const heroStyle =
    heroResolved && useHeroBackgroundPhoto
      ? {
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.3)), url(${JSON.stringify(heroResolved)})`
        }
      : undefined;

  return (
    <div className="home">
      <section
        className={`hero${heroImageUrl && useHeroBackgroundPhoto ? ' hero--has-custom' : ''}`}
        style={heroStyle}
      >
        <div className="hero-content">
        </div>
      </section>

      <section className="news-section">
        <div className="container">
          <h2 className="section-title">{t('home.rssTitle')}</h2>
          
          {/* RSS Tuttosport — 4 cartes (sans défilement) */}
          {rssLoading ? (
            <p className="rss-news-cards-loading">{t('home.rssLoading')}</p>
          ) : (
            rssArticles.length > 0 && (
              <div className="rss-news-cards">
                {rssArticles.map((article, index) => (
                  <article key={`${article.link}-${index}`} className="rss-news-card">
                    <div className="rss-news-card-image">
                      {article.image ? (
                        <img src={article.image} alt={article.title} loading="lazy" />
                      ) : (
                        <div className="rss-news-card-image-placeholder" aria-hidden />
                      )}
                    </div>
                    <div className="rss-news-card-body">
                      <h3 className="rss-news-card-title">{article.title}</h3>
                      <a
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rss-news-card-btn"
                      >
                        {t('home.readArticle')}
                      </a>
                    </div>
                  </article>
                ))}
              </div>
            )
          )}
          
          {loading ? (
            <div className="loading">{t('home.loading')}</div>
          ) : (
            <>
              <div className="news-grid">
                {news.map((item) => (
                  <NewsCard key={item._id} news={item} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    {t('home.prev')}
                  </button>
                  <span className="pagination-info">
                    {t('home.pageOf', { page: currentPage, total: totalPages })}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    {t('home.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Upcoming Match Section */}
      <section className="upcoming-match-section">
        <div className="container">
          <h2 className="section-title">{t('home.upcomingTitle')}</h2>
          <InactiveMemberNotice />
          {matchLoading ? (
            <div className="loading">{t('home.loading')}</div>
          ) : upcomingMatches.length > 0 ? (
            <div className="home-next-match tickets-grid tickets-grid-horizontal">
              {upcomingMatches.map((match) => renderUpcomingMatchCard(match))}
            </div>
          ) : (
            <div className="no-match">
              <p>{t('home.noMatch')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Serie A standings */}
      <section className="standings-section">
        <div className="container">
          <h2 className="section-title">{t('home.standingsTitle')}</h2>
          {standingsLoading ? (
            <div className="loading">{t('home.standingsLoading')}</div>
          ) : standings.length > 0 ? (
            <div className="standings-wrapper">
              <table className="standings-table">
                <thead>
                  <tr>
                    <th>{t('home.standingsThRank')}</th>
                    <th className="standings-team">{t('home.standingsThTeam')}</th>
                    <th>{t('home.standingsThJ')}</th>
                    <th>{t('home.standingsThG')}</th>
                    <th>{t('home.standingsThN')}</th>
                    <th>{t('home.standingsThP')}</th>
                    <th>{t('home.standingsThGF')}</th>
                    <th>{t('home.standingsThGA')}</th>
                    <th>{t('home.standingsThGD')}</th>
                    <th>{t('home.standingsThPts')}</th>
                    <th>{t('home.standingsThForm')}</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((row) => (
                    <tr key={row.idStanding} className={row.strTeam === 'Juventus' ? 'standings-row-juve' : ''}>
                      <td>{row.intRank}</td>
                      <td className="standings-team">
                        {row.strBadge && (
                          <img src={row.strBadge} alt="" className="standings-badge" />
                        )}
                        <span>{row.strTeam}</span>
                      </td>
                      <td>{row.intPlayed}</td>
                      <td>{row.intWin}</td>
                      <td>{row.intDraw}</td>
                      <td>{row.intLoss}</td>
                      <td>{row.intGoalsFor}</td>
                      <td>{row.intGoalsAgainst}</td>
                      <td className="standings-gd">{row.intGoalDifference > 0 ? `+${row.intGoalDifference}` : row.intGoalDifference}</td>
                      <td className="standings-pts"><strong>{row.intPoints}</strong></td>
                      <td className="standings-form">{row.strForm || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="standings-legend">{t('home.standingsLegend')}</p>
            </div>
          ) : (
            <div className="loading">{t('home.standingsEmpty')}</div>
          )}
        </div>
      </section>

      <section className="membership-section">
        <div className="container">
          <div className="membership-card">
            <h2>{t('home.membershipTitle')}</h2>
            <p>{t('home.membershipSubtitle')}</p>
            <a href="/register" className="btn-membership">{t('home.membershipCta')}</a>
          </div>
        </div>
      </section>

      <section className="help-section">
        <div className="container">
          <h2 className="section-title help-section-title">
              <FaInfoCircle className="help-section-icon" aria-hidden />
              {t('home.helpTitle')}
            </h2>
          <div className="help-content">
            <div className="help-block">
              <h3>{t('home.helpTicketShopTitle')}</h3>
              <p>
                {t('home.helpTicketShopP1')}{' '}
                <a href="https://tickets.juventus.com/it/calendar/subscription/fromHeader" target="_blank" rel="noopener noreferrer" className="help-link">
                  {t('home.helpTicketShopClick')}
                </a>
              </p>
              <p>
                {t('home.helpTicketShopP2')}
              </p>
              <p>
                {t('home.helpTicketShopP3')}
                <a href="https://tickets.juventus.com/it/calendar/authentication/?back=fromHeader" target="_blank" rel="noopener noreferrer" className="help-link">
                  {t('home.helpTicketShopClick')}
                </a>
              </p>
              <p>
                {t('home.helpTicketShopP4')}
              </p>
            </div>
            <div className="help-block">
              <h3>{t('home.helpFanTitle')}</h3>
              <p className="help-phone">
                <a href="tel:+390114530486" className="help-link">+39 011 453 0486</a>
              </p>
              <p>
                {t('home.helpFanP')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="partners-section">
        <div className="container">
          <h2 className="section-title">
            <Link to="/partenaires" className="partners-section-title-link">
              {t('footer.partnersSubtitle')}
            </Link>
          </h2>
          <div className="partners-slider-wrapper">
            <div
              className="partners-slider"
              style={
                partnersMarqueeDurationSec
                  ? { animationDuration: `${partnersMarqueeDurationSec}s` }
                  : undefined
              }
            >
              {[...partnersWithLogos, ...partnersWithLogos].map((partner, index) => {
                const imagePath = resolvePartnerLogoUrl(partner.logoUrl);
                const content = (
                  <div className="partner-slide">
                    <img 
                      src={imagePath} 
                      alt={partner.name}
                      className="partner-slide-image"
                    />
                  </div>
                );

                if (partner.websiteUrl) {
                  return (
                    <a
                      key={`${partner._id}-${index}`}
                      href={partner.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="partner-slide-link"
                      title={partner.name}
                    >
                      {content}
                    </a>
                  );
                }

                return (
                  <div key={`${partner._id}-${index}`} className="partner-slide-link" title={partner.name}>
                    {content}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;


